/**
 * Admin pricing-config API — the admin-gated `/correspondents/admin/*` routes
 * (`app/routers/correspondents.py`), documented in
 * frontend_documentation/pricing-config-and-lock-extensions.md §1–§2.
 *
 * Core principle from that doc: NO pricing values are hardcoded. DSCR tier
 * margins and RTL base rates are read from MongoDB and edited here; every
 * upsert appends a `versionHistory` entry (who/when/old→new) and publishes to
 * the `pricing_updates` Service Bus topic.
 *
 * Scales are explicit and never mix in one field:
 *   • RTL base rates = annual DECIMAL FRACTION (0.105 = 10.50%)
 *   • RTL tier spreads = annual PERCENT (0.25 = 0.25% on the rate)
 *   • DSCR tier margins = BOND POINTS (par = 100; 0.375 = Gold margin)
 *
 * A non-admin gets 403 (`admin_required`) from every call below.
 */
import { losHttp } from "../lib/losHttp";

const BASE = "/correspondents/admin";

export const adminTierMarginsApi = {
  // GET /correspondents/admin/tier_margins → one row per tier
  // (Gold/Silver/Bronze/Base) with marginPoints, max/minPrice, maxPriceShortPPP,
  // min/maxLoanAmount, label, description.
  list: () =>
    losHttp.get(`${BASE}/tier_margins`).then((r) => {
      const d = r.data;
      if (Array.isArray(d)) return d;
      return d?.tiers || d?.tierMargins || [];
    }),

  // GET /correspondents/admin/tier_margins/{tier}/history → append-only history
  history: (tier) =>
    losHttp
      .get(`${BASE}/tier_margins/${encodeURIComponent(tier)}/history`)
      .then((r) => r.data?.versionHistory || []),

  // POST /correspondents/admin/tier_margins/{tier} — upsert the FULL config.
  // `marginPoints` is required (400 otherwise).
  upsert: (tier, body) =>
    losHttp
      .post(`${BASE}/tier_margins/${encodeURIComponent(tier)}`, body)
      .then((r) => r.data),
};

// The RTL product taxonomy the sheet is keyed by. The API echoes this as
// `products` so the grid can render rows for products that aren't seeded yet —
// prefer the server list and fall back to this.
export const RTL_BASE_RATE_PRODUCTS = [
  { key: "fix_and_flip", label: "Fix and Flip" },
  {
    key: "fix_and_flip_heavy_rehab",
    label: "Fix and Flip – Heavy Rehab",
    secondary_of: "fix_and_flip",
  },
  { key: "ground_up_construction", label: "Ground Up Construction" },
  { key: "bridge", label: "Bridge" },
];

export const adminRtlBaseRatesApi = {
  // GET /correspondents/admin/rtl_base_rates
  //   → { rows: [{ product, label, ratesByTerm, notes, ... }], products: [...] }
  // Rates are seeded EMPTY — an empty row means no base rate (locks show no buy
  // rate until the desk enters one).
  list: () =>
    losHttp.get(`${BASE}/rtl_base_rates`).then((r) => ({
      rows: r.data?.rows || [],
      products: r.data?.products || RTL_BASE_RATE_PRODUCTS,
    })),

  // GET /correspondents/admin/rtl_base_rates/{product}/history
  history: (product) =>
    losHttp
      .get(`${BASE}/rtl_base_rates/${encodeURIComponent(product)}/history`)
      .then((r) => r.data?.versionHistory || []),

  // POST /correspondents/admin/rtl_base_rates/{product}
  //   body: { label?, ratesByTerm: {"12": 0.105, ...}, notes? }
  // `ratesByTerm` keys are term-in-months STRINGS; values decimal fractions.
  upsert: (product, body) =>
    losHttp
      .post(`${BASE}/rtl_base_rates/${encodeURIComponent(product)}`, body)
      .then((r) => r.data),
};

// The tier names the backend seed creates and the ONLY options the correspondent
// `rtl_pricing_tier` field offers — copied verbatim from `DSCR_TIER_NAMES` in
// app/models/correspondents.py (that model isn't served by /model_schemas, so
// there is no live enum to read). The admin tier editor is limited to these.
// The seed note: a tier at spreadPct 0 carrying it is "not yet priced", not free.
export const RTL_TIER_SEED_NAMES = ["Gold", "Silver", "Bronze"];
export const RTL_TIER_SEED_NOTE = "seed - desk sets values";

// The default RTL / GUC account spread per tier, in PERCENT added to the base
// rate (0.125 = +0.125%) — set by the desk 2026-10-08. Used when a tier is
// created and to prefill the Add tier dialog. The backend applies the same
// defaults itself (its seed, and an upgrade of rows still at the old zero seed
// on first read — `c1ca920`), so this mirror only covers the FE create path.
// The tier table stays the source of truth once a value is saved.
export const RTL_TIER_DEFAULT_SPREADS = { Gold: 0.125, Silver: 0.25, Bronze: 0.375 };
export const RTL_TIER_DEFAULT_NOTE = "default spread (desk, 2026-10-08)";

export const adminRtlTierSpreadsApi = {
  // GET /correspondents/admin/rtl_tier_spreads → { rows: [...] }, one row per
  // tier with spreadPct (PERCENT, 0.25 = 0.25%), label, min/maxLoanAmount,
  // active, description, notes and its full versionHistory.
  list: () =>
    losHttp
      .get(`${BASE}/rtl_tier_spreads`)
      .then((r) => (Array.isArray(r.data) ? r.data : r.data?.rows || [])),

  // GET /correspondents/admin/rtl_tier_spreads/{tier}/history
  history: (tier) =>
    losHttp
      .get(`${BASE}/rtl_tier_spreads/${encodeURIComponent(tier)}/history`)
      .then((r) => r.data?.versionHistory || []),

  // POST /correspondents/admin/rtl_tier_spreads/{tier} — `spreadPct` required
  // (400 otherwise). Sent exactly as typed: percent, never scaled.
  upsert: (tier, body) =>
    losHttp
      .post(`${BASE}/rtl_tier_spreads/${encodeURIComponent(tier)}`, body)
      .then((r) => r.data),

  // Create the missing Gold / Silver / Bronze tiers AT THEIR DEFAULT SPREADS
  // (RTL_TIER_DEFAULT_SPREADS). Never touches a tier that already exists.
  //
  // Not the backend's `POST /rtl_tier_spreads/seed`: it now seeds the same
  // defaults, but it is still unreachable (declared after `/{tier}`, so it is
  // matched as a tier named "seed" and 400s). Tiers are created through the
  // per-tier upsert instead.
  seed: async (existingTiers = []) => {
    const have = new Set(existingTiers);
    const created = [];
    for (const tier of RTL_TIER_SEED_NAMES) {
      if (have.has(tier)) continue;
      // eslint-disable-next-line no-await-in-loop
      await losHttp.post(`${BASE}/rtl_tier_spreads/${tier}`, {
        label: tier,
        spreadPct: RTL_TIER_DEFAULT_SPREADS[tier],
        active: true,
        notes: RTL_TIER_DEFAULT_NOTE,
      });
      created.push(tier);
    }
    return { created, existing: [...have].sort() };
  },
};

export const adminPricingConfigApi = {
  tierMargins: adminTierMarginsApi,
  rtlBaseRates: adminRtlBaseRatesApi,
  rtlTierSpreads: adminRtlTierSpreadsApi,
};

export default adminPricingConfigApi;
